import React, { useEffect, useState } from "react";
import Select from "react-select";
import {
    Badge,
    Button,
    FormGroup,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Spinner,
} from "reactstrap";
import { submitAIAddonCallTemplateFilterData } from "model/AIAddonCallTemplateModel";
import { getAiRecruiterCallTriggerStatus } from "model/AiRecruiterLogModel";

const reactSelectMenuPortalStyles = {
    menuPortal: (baseStyles) => ({ ...baseStyles, zIndex: 9999 }),
    menu: (baseStyles) => ({ ...baseStyles, zIndex: 9999 }),
};

const mapTemplateRowsToSelectOptions = (rows = []) =>
    (Array.isArray(rows) ? rows : [])
        .map((row) => {
            const value = String(row?.id ?? "").trim();
            const label = String(row?.note ?? "").trim();
            if (!value || !label) return null;
            return { value, label };
        })
        .filter(Boolean);

const CallDetailsAiCallRequestModal = ({
    isOpen,
    onClose,
    onConfirm,
    submitting = false,
    candidateName = "",
    candidateClientId = "",
}) => {
    const [templateOptions, setTemplateOptions] = useState([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [aiCallStatus, setAiCallStatus] = useState("");
    const [statusLoading, setStatusLoading] = useState(false);

    useEffect(() => {
        if (!isOpen) return;

        setSelectedTemplate(null);
        setAiCallStatus("");
        setTemplateOptions([]);

        let requestCancelled = false;

        (async () => {
            setTemplatesLoading(true);
            setStatusLoading(true);

            const id = String(candidateClientId ?? "").trim();

            try {
                const [templatesResponse, statusResponse] = await Promise.all([
                    submitAIAddonCallTemplateFilterData({}),
                    id ? getAiRecruiterCallTriggerStatus(id) : Promise.resolve(null),
                ]);

                if (requestCancelled) return;

                const templatesData = templatesResponse?.data;
                const templateRows = Array.isArray(templatesData?.records)
                    ? templatesData.records
                    : Array.isArray(templatesData)
                      ? templatesData
                      : [];
                setTemplateOptions(mapTemplateRowsToSelectOptions(templateRows));

                const statusValue = String(statusResponse?.data?.status ?? "").trim();
                setAiCallStatus(statusValue);
            } catch {
                if (!requestCancelled) {
                    setTemplateOptions([]);
                    setAiCallStatus("");
                }
            } finally {
                if (!requestCancelled) {
                    setTemplatesLoading(false);
                    setStatusLoading(false);
                }
            }
        })();

        return () => {
            requestCancelled = true;
        };
    }, [isOpen, candidateClientId]);

    const handleClose = () => {
        if (!submitting) onClose?.();
    };

    const handleTemplateChange = (option) => {
        setSelectedTemplate(option);
    };

    const handleConfirmClick = () => {
        onConfirm?.({
            aiAddOnCallTemplateId: String(selectedTemplate?.value ?? "").trim(),
            status: String(aiCallStatus ?? "").trim(),
        });
    };

    const statusLabel =
        aiCallStatus != null && String(aiCallStatus).trim() !== ""
            ? String(aiCallStatus).trim()
            : "—";

    const isBusy = submitting || templatesLoading || statusLoading;

    return (
        <Modal
            isOpen={isOpen}
            toggle={handleClose}
            centered
            backdrop="static"
            size="md"
            zIndex={5000}
            className="cd-ai-call-request-modal modal-full-height"
        >
            <ModalHeader toggle={handleClose} className="cd-ai-call-request-modal__header">
                <span className="cd-ai-call-request-modal__title">
                    <i className="mdi mdi-phone-voip me-2" aria-hidden />
                    AI Call Request
                </span>
            </ModalHeader>
            <ModalBody className="cd-ai-call-request-modal__body">
                <p className="mb-3 text-muted">
                    Trigger an AI recruiter call for this candidate?
                </p>
                {candidateName ? (
                    <div className="cd-ai-call-request-summary">
                        <div className="cd-ai-call-request-summary__row">
                            <span className="cd-ai-call-request-summary__label">Candidate</span>
                            <span className="cd-ai-call-request-summary__value fw-semibold">
                                {candidateName}
                            </span>
                        </div>
                        <div className="cd-ai-call-request-summary__row">
                            <span className="cd-ai-call-request-summary__label">Status</span>
                            {statusLoading ? (
                                <Spinner size="sm" />
                            ) : (
                                <Badge
                                    color={
                                        String(statusLabel).toLowerCase() === "deactive"
                                            ? "warning"
                                            : "primary"
                                    }
                                    className="badge-padding text-capitalize"
                                >
                                    {statusLabel}
                                </Badge>
                            )}
                        </div>
                    </div>
                ) : null}

                <FormGroup className="mt-3 mb-0">
                    <Label>AI Add-On Call Template</Label>
                    <Select
                        className="react-select-container"
                        classNamePrefix="react-select"
                        options={templateOptions}
                        value={selectedTemplate}
                        onChange={handleTemplateChange}
                        isLoading={templatesLoading}
                        isDisabled={submitting}
                        isClearable
                        placeholder="Select template"
                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                        styles={reactSelectMenuPortalStyles}
                        menuPosition="fixed"
                    />
                </FormGroup>
            </ModalBody>
            <ModalFooter className="cd-ai-call-request-modal__footer">
                <Button color="secondary" type="button" onClick={handleClose} disabled={submitting}>
                    Cancel
                </Button>
                <Button
                    color="primary"
                    type="button"
                    onClick={handleConfirmClick}
                    disabled={isBusy}
                >
                    {submitting ? (
                        <>
                            <Spinner size="sm" className="me-1" />
                            Submitting…
                        </>
                    ) : (
                        "OK"
                    )}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default CallDetailsAiCallRequestModal;
